import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { RIDE_TYPES, money, when } from "@/lib/rides";
import { DAY_NAMES, dayTime } from "@/lib/rider";
import { Stops } from "@/components/RideStatus";
import { OnlineToggle, RideRow } from "@/components/driver/DriverHome";
import { Segmented, SectionTitle } from "@/components/rider/Nav";
import { Button } from "@/components/ui/button";

export const useNowRides = () => useQuery({ queryKey: ["nowRides"], queryFn: () => api.get("/driver/now-rides").then((r) => r.data), refetchInterval: 10000 });
export const useAvailableSets = () => useQuery({ queryKey: ["availableSets"], queryFn: () => api.get("/driver/ride-sets/available").then((r) => r.data), refetchInterval: 30000 });
export const useMySets = () => useQuery({ queryKey: ["mySets"], queryFn: () => api.get("/driver/ride-sets/mine").then((r) => r.data), refetchInterval: 30000 });
const REFRESH = ["nowRides", "availableSets", "mySets", "driverState", "driverRides", "driverActive", "driverCalendar"];

function useAct() {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const run = async (id, path, msg) => {
    setBusy(id);
    try {
      const { data } = await api.post(path);
      toast.success(msg(data));
      REFRESH.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      toast.error(formatApiError(e), { duration: 9000 });
    } finally {
      setBusy("");
    }
  };
  return { busy, run };
}

export function NowRides() {
  const { data } = useNowRides();
  const { busy, run } = useAct();
  return (
    <div className="space-y-4" data-testid="now-rides">
      <h1 className="font-display text-2xl font-bold tracking-tight">Now Rides</h1>
      <OnlineToggle />
      {!data?.online && <p className="text-sm text-muted-foreground" data-testid="now-offline">Go Online to see immediate ride requests.</p>}
      {data?.online && !data.rides.length && <p className="text-sm text-muted-foreground" data-testid="now-empty">No Now Ride requests right now. We'll notify you.</p>}
      {data?.rides.map((r) => (
        <article key={r.id} className="space-y-3 rounded-2xl border border-white/10 bg-card p-4" data-testid={`now-ride-${r.id}`}>
          <div className="flex justify-between"><p className="text-sm font-semibold">{r.rider_name} · {r.passengers} pax</p><p className="font-display text-lg font-bold text-primary">{money(r.fare)}</p></div>
          <Stops ride={r} />
          <p className="text-xs text-muted-foreground">Pickup {when(r.scheduled_at)} · {RIDE_TYPES[r.trip_type]}{r.status === "pending" ? " · accepting also approves it" : ""}</p>
          <Button className="h-11 w-full rounded-full font-semibold" disabled={!!busy || !!data.lock} onClick={() => run(r.id, `/driver/now-rides/${r.id}/accept`, () => "Ride accepted. It's on your schedule.")} data-testid={`accept-now-${r.id}`}>
            {busy === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : data.lock ? "Locked until your scheduled ride is done" : "Accept Now Ride"}
          </Button>
        </article>
      ))}
    </div>
  );
}

const KIND = { split_trip: "Split Trip", one_way: "One-Way", round_trip: "Round Trip", multi_stop: "Multi-Stop" };

function SetHeader({ s }) {
  return (
    <>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wider text-primary">{KIND[s.kind] || s.kind}{s.recurring ? " · Recurring" : ""}</p>
          <p className="font-display text-lg font-bold">{s.rider_name}</p>
        </div>
        <span className="rounded-full bg-primary/15 px-2.5 py-1 text-xs font-bold text-primary" data-testid={`set-legs-${s.key}`}>{s.legs} ride leg{s.legs > 1 ? "s" : ""}</span>
      </div>
      {s.routes.map((r, i) => <p key={i} className="text-sm">{s.routes.length > 1 ? `Leg ${i + 1}: ` : ""}{r.pickup} → {r.dropoff} · {dayTime(r.time).time}</p>)}
      <p className="text-xs text-muted-foreground">
        {s.recurring && `${s.days.map((d) => DAY_NAMES[d]).join(", ")} · `}{dayTime(s.first_at).day}{s.legs > 1 ? ` to ${dayTime(s.last_at).day}` : ""}{s.stops ? ` · ${s.stops} stops` : ""}
      </p>
    </>
  );
}

function Schedule({ s, onOpen }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-xs font-semibold text-primary" data-testid={`set-view-${s.key}`}>
        {open ? "Hide" : "View"} full schedule ({s.rides.length}) <ChevronDown className="h-3 w-3" />
      </button>
      {open && <div className="space-y-2" data-testid={`set-schedule-${s.key}`}>{s.rides.map((r) => <RideRow key={r.id} ride={r} onOpen={onOpen} />)}</div>}
    </>
  );
}

function AvailableSet({ s, onOpen }) {
  const { busy, run } = useAct();
  const [sure, setSure] = useState(false);
  return (
    <article className="space-y-3 rounded-2xl border border-white/10 bg-card p-4" data-testid={`available-set-${s.key}`}>
      <SetHeader s={s} />
      <Schedule s={s} onOpen={onOpen} />
      {sure ? (
        <div className="space-y-2 rounded-xl border border-primary/40 p-3">
          <p className="text-xs">You're committing to all {s.legs} rides in this set. You can't drop single legs. Only Scoopz Admin can split a set.</p>
          <Button className="h-11 w-full rounded-full font-semibold" disabled={!!busy} data-testid={`confirm-accept-set-${s.key}`}
            onClick={() => run(s.key, `/driver/ride-sets/${encodeURIComponent(s.key)}/accept`, (d) => `${d.taken.length} rides accepted${d.failed.length ? `, ${d.failed.length} need admin attention` : ""}`)}>
            {busy ? <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Assigning {s.legs} rides…</span> : `Confirm: accept all ${s.legs}`}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <Button className="rounded-full font-semibold" onClick={() => setSure(true)} data-testid={`accept-set-${s.key}`}>Accept Entire Set</Button>
          <Button variant="ghost" className="rounded-full text-muted-foreground" disabled={!!busy} data-testid={`decline-set-${s.key}`}
            onClick={() => run(s.key, `/driver/ride-sets/${encodeURIComponent(s.key)}/decline`, () => "Set hidden")}>Decline</Button>
        </div>
      )}
    </article>
  );
}

function MySet({ s, onOpen }) {
  return (
    <article className="space-y-3 rounded-2xl border border-white/10 bg-card p-4" data-testid={`my-set-${s.key}`}>
      <SetHeader s={s} />
      <p className="text-xs" data-testid={`my-set-counts-${s.key}`}>{s.upcoming} upcoming · {s.completed} completed</p>
      {s.reassigned_away > 0 && <p className="rounded-xl bg-amber-400/10 px-3 py-2 text-xs text-amber-200" data-testid={`my-set-reassigned-${s.key}`}>Scoopz Admin reassigned {s.reassigned_away} leg{s.reassigned_away > 1 ? "s" : ""} of this set to another driver.</p>}
      {s.changes?.length > 0 && <p className="text-[11px] text-muted-foreground">Recent schedule changes: {s.changes.map((c) => `${c.note} ${when(c.at)}`).join("; ")}</p>}
      <Schedule s={s} onOpen={onOpen} />
    </article>
  );
}

export function ScheduledRides({ tab, setTab, onOpen }) {
  const { data: avail, isLoading } = useAvailableSets();
  const { data: mine } = useMySets();
  const list = tab === "available" ? avail : mine;
  return (
    <div className="space-y-4" data-testid="scheduled-rides">
      <h1 className="font-display text-2xl font-bold tracking-tight">Scheduled Rides</h1>
      <Segmented testid="sets-tab" value={tab} onChange={setTab} options={[{ value: "available", label: `AVAILABLE (${avail?.length ?? 0})` }, { value: "mine", label: `MY SETS (${mine?.length ?? 0})` }]} />
      <SectionTitle>{tab === "available" ? "Available ride sets" : "My accepted ride sets"}</SectionTitle>
      {isLoading && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
      {!list?.length && !isLoading && <p className="text-sm text-muted-foreground" data-testid="sets-empty">{tab === "available" ? "No ride sets are waiting for a driver." : "You haven't accepted any ride sets yet."}</p>}
      {list?.map((s) => (tab === "available" ? <AvailableSet key={s.key} s={s} onOpen={onOpen} /> : <MySet key={s.key} s={s} onOpen={onOpen} />))}
    </div>
  );
}
